import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import SvgIcon from "../../assets/icons/SvgIcon";
import {
  ModalBodyProps,
  ModalBoxProps,
  ModalHeaderProps,
} from "../../types/modal";
import useDisableScroll from "../../hooks/useDisableScrollBar";

const ModalBox: React.FC<ModalBoxProps> = ({
  children,
  show = false,
  horizontalAlign = "Center",
  verticalAlign = "Center",
  closeModal,
  closeIcon = true,
  outSideClick = false,
  boxSize = "max-w-xl",
  classList,
  className = "",
}) => {
  const boxModal = useRef<HTMLDivElement>(null);

  useDisableScroll(show);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      closeModal &&
      boxModal.current &&
      !boxModal.current.contains(event.target as Node)
    ) {
      closeModal();
    }
  };

  useEffect(() => {
    if (show && outSideClick) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show]);

  let horizontalAlignmentClass = "justify-center";
  if (horizontalAlign === "Left") horizontalAlignmentClass = "justify-start";
  if (horizontalAlign === "Right") horizontalAlignmentClass = "justify-end";

  let verticalAlignmentClass = "items-center";
  if (verticalAlign === "Top") verticalAlignmentClass = "items-start";
  if (verticalAlign === "Bottom") verticalAlignmentClass = "items-end";

  const modalContent = show ? (
    <div
      className={`${className} main-modal-box relative z-[9999999] ${show ? "block" : "hidden"}`}
    >
      <div
        className={`fixed inset-0 bg-slate-800/10 bg-opacity-75 transition-all duration-700 ${show ? "block" : "hidden"}`}
      ></div>

      <div
        className={`fixed inset-0 z-10 w-screen overflow-y-auto p-4 ${classList?.modalWidth}`}
      >
        <div
          className={`flex min-h-full p-4 text-start items-start sm:p-0 ${verticalAlignmentClass} ${horizontalAlignmentClass} ${classList?.modalOutSideClassName}`}
        >
          <div
            ref={boxModal}
            className={`${show ? "translate-y-0 opacity-100 visible" : "-translate-y-full opacity-0 invisible"} relative transform overflow-visible rounded-lg bg-red text-left shadow-xl transition-all sm:my-8 w-full ${boxSize} ${classList?.modalClassName}`}
          >
            <div className="relative bg-white rounded-md h-full">
              {closeIcon && (
                <button
                  type="button"
                  className="top-4 right-4 z-10 absolute cursor-pointer close-button"
                  onClick={closeModal}
                >
                  <SvgIcon iconName="close" className="text-2xl" />
                </button>
              )}
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // Render the modal content in the portal
  return typeof document !== "undefined"
    ? ReactDOM.createPortal(modalContent, document.getElementById("root")!)
    : null;
};

export default ModalBox;

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  children,
  className = "",
}) => {
  return <div className={`relative ${className}`}>{children}</div>;
};

export const ModalBody: React.FC<ModalBodyProps> = ({
  children,
  className = "",
}) => {
  return <div className={`py-4 ${className}`}>{children}</div>;
};
